import FilesList from 'components/v2/file-row/FilesList';
import { ExpandableHelpHtml } from 'components/v2/help-boxes/ExpandableHelpHtml';
import React from 'react';
import useProperty from '../hooks/useProperty';
import usePropertyPath from '../hooks/usePropertyPath';
import { FieldPanelFullWidth } from 'styles/v2/Styles.styled';
import FileUpload from 'components/v2/file-upload/FileUpload';
import { ButtonUploadIcon } from 'components/v2/icons';
import { deleteUploadFile, postUploadFile } from 'services/api/dynamicFormsFilesApi';
import { MAX_FILE_SIZE } from 'constants/constants';
import { toaster } from 'components/v2/toast';
import { getEpdWriteBlockedMessage } from 'util/epdActionErrorUtils';
import { UploadFileResult } from 'types/dynamic-forms/files/UploadFileResult';
import { t } from 'i18next';
import { ChangeEvent } from 'react';
import { useDocumentStore } from '../hooks/useDocumentStore';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { getFirstOrDefault } from '../utils/getFirstOrDefault';

const SingleFileUploadRenderer: React.FC<any> = ({
  propertyId,
  helpText,
  label,
  tooltip,
  supportedFileExtensions,
  uploadFileType,
  required,
}) => {
  const store = useDocumentStore();
  const { docId } = store;
  const propertyPath = usePropertyPath(propertyId);
  const { value, setValue, isVisible, isDisabled, errorMessage } = useProperty<Record<string, any>>(propertyPath);
  const nestedErrorMessage = getFirstOrDefault(store.getErrorMessagesIncludingNested(propertyPath));
  const displayError = errorMessage ?? nestedErrorMessage;

  // Visibility guard - follows TextInputRenderer pattern
  if (!isVisible) {
    return null;
  }

  const normalizedExtensions = Array.isArray(supportedFileExtensions)
    ? supportedFileExtensions
        .map((x: string) => x?.toString().trim().toLowerCase().replace(/^\./, ''))
        .filter((x: string) => x.length > 0)
    : ['pdf'];
  const acceptedExtensions = normalizedExtensions.map((x: string) => `.${x}`).join(',');
  const renderedHelpText = (helpText ?? tooltip ?? '').toString();
  const uploadPropertyName = (uploadFileType ?? propertyId).toString();
  const hasFile = value?.id != null;

  const handleRemove = (id: string) => {
    void (async () => {
      try {
        await deleteUploadFile(docId, id, propertyId);
        setValue({});
      } catch (error) {
        toaster({
          severity: 'error',
          summary: 'Error',
          details: getEpdWriteBlockedMessage(error) ?? 'Could not remove the file. Please try again.',
        });
      }
    })();
  };

  const uploadFile = async (docId: string, propertyName: string, file: File | undefined) => {
    if (!file) {
      return null;
    }

    if (file.size > MAX_FILE_SIZE) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: t('epdWizard.messages.errorTooLargeFile') as string,
      });
      return;
    }

    let result: UploadFileResult;
    try {
      result = await store.synchronize(() =>
        postUploadFile(docId, {
          propertyName: propertyName,
          propertyId,
          docId: docId,
          file: file,
          documentTypeId: null,
          documentLanguageCode: null,
        })
      );
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'Could not upload the file. Please try again.',
      });
      return;
    }

    if (result.isSuccessful) {
      // Set single object (not array append)
      const fileObject: Record<string, any> = {
        id: result.model.fileId,
        name: result.model.fileName || file.name,
      };
      setValue(fileObject);
    } else {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: result.message || 'Could not upload the file. Please try again.',
      });
    }
  };

  return (
    <>
      <FieldPanelFullWidth style={{ gridRowGap: 0, padding: 0 }}>
        <SmartLabel label={label} required={required} hasValue={hasFile} error={displayError} />
        {!isDisabled && !hasFile && (
          <>
            {renderedHelpText && <ExpandableHelpHtml key="help" html={renderedHelpText} />}
            <FileUpload
              label="Upload document"
              name={uploadPropertyName}
              icon={ButtonUploadIcon}
              accept={acceptedExtensions}
              validExtensions={normalizedExtensions}
              disabled={false}
              onFileUpload={(e: ChangeEvent<HTMLInputElement>, propertyName: string) =>
                uploadFile(docId, propertyName, e.target.files?.[0] ?? undefined)
              }
            />
          </>
        )}
        <FilesList
          files={hasFile ? [{ ...(value ?? {}), isRemovable: !isDisabled }] : []}
          disabled={isDisabled}
          onRemoveFile={isDisabled ? undefined : handleRemove}
        />
      </FieldPanelFullWidth>
    </>
  );
};

export default SingleFileUploadRenderer;
